(() => {
  const config = window.JIMobileCartConfirmation || {};
  const mobileMedia = window.matchMedia('(max-width: 699px)');
  const productCache = new Map();
  let overlay = null;
  let lastFocusedElement = null;
  let recommendationsPromise = null;

  if (!config.productId || !config.productHandle) return;

  const rootUrl = normalizeRoot(config.rootUrl || '/');

  onReady(() => {
    document.addEventListener('submit', onProductFormSubmit, true);

    if (mobileMedia.addEventListener) {
      mobileMedia.addEventListener('change', (event) => {
        if (!event.matches) closeConfirmation();
      });
    }
  });

  async function onProductFormSubmit(event) {
    if (!mobileMedia.matches) return;

    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !isProductForm(form)) return;

    const submitter = event.submitter;
    if (submitter && (submitter.name === 'checkout' || submitter.closest('.shopify-payment-button'))) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    if (form.dataset.jiMobileCartAdding === 'true') return;

    form.dataset.jiMobileCartAdding = 'true';
    setSubmitterBusy(submitter, true);

    try {
      const addedItem = await addProductFromForm(form);
      await refreshCartCount();
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      showConfirmation(addedItem);
    } catch (error) {
      console.warn('JI mobile cart confirmation failed', error);
      window.alert('Das Produkt konnte gerade nicht in den Warenkorb gelegt werden. Bitte versuche es erneut.');
    } finally {
      delete form.dataset.jiMobileCartAdding;
      setSubmitterBusy(submitter, false);
    }
  }

  function isProductForm(form) {
    const action = form.getAttribute('action') || '';
    return form.matches('form[is="product-form"]') || action.includes('/cart/add') || form.id.includes('product-form') || form.id.includes('quick-buy-form');
  }

  async function addProductFromForm(form) {
    const formData = new FormData(form);
    const resolved = await resolveVariant(form, formData);
    const variantId = resolved.id;

    if (!variantId) throw new Error('No variant ID found for product form.');

    const payload = {
      id: Number(variantId),
      quantity: getQuantity(form, formData)
    };

    const sellingPlan = formData.get('selling_plan');
    if (sellingPlan) payload.selling_plan = sellingPlan;

    const properties = {};
    for (const [key, value] of formData.entries()) {
      if (!key.startsWith('properties[') || value === '') continue;
      const propertyName = key.slice(11, -1);
      if (propertyName) properties[propertyName] = value;
    }

    if (Object.keys(properties).length > 0) payload.properties = properties;

    const response = await fetch(toAjaxCartAddUrl(config.cartAddUrl), {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.description || data.message || 'Cart add failed.');

    return normalizeAddedItem(data, resolved, payload.quantity);
  }

  async function resolveVariant(form, formData) {
    const picker = findVariantPicker(form);
    let variant = readPickerVariant(picker);
    let id = variant && variant.id ? variant.id : null;

    const formVariantId = formData.get('id') || form.querySelector('[name="id"]:not([disabled])')?.value;
    if (!id && formVariantId) id = formVariantId;

    const urlVariantId = new URLSearchParams(window.location.search).get('variant');
    if (!id && urlVariantId) id = urlVariantId;

    const product = await getProductJson(picker?.getAttribute('handle') || config.productHandle);

    if (product) {
      if (id) {
        variant = product.variants.find((item) => String(item.id) === String(id)) || variant;
      } else {
        variant = findVariantBySelectedOptions(product, form, picker) || variant;
        id = variant && variant.id ? variant.id : null;
      }
    }

    return { id: id ? String(id) : '', variant, product };
  }

  function findVariantPicker(form) {
    if (!form.id) return document.querySelector('variant-picker');

    const escapedFormId = cssEscape(form.id);
    return document.querySelector(`variant-picker[form-id="${escapedFormId}"]`) || document.querySelector('variant-picker');
  }

  function readPickerVariant(picker) {
    const script = picker?.querySelector('script[data-variant]');
    if (!script) return null;

    try {
      return JSON.parse(script.textContent || 'null');
    } catch (error) {
      return null;
    }
  }

  async function getProductJson(handle) {
    if (!handle) return null;
    if (productCache.has(handle)) return productCache.get(handle);

    const promise = fetch(`${rootUrl}products/${encodeURIComponent(handle)}.js`, {
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' }
    }).then((response) => response.ok ? response.json() : null).catch(() => null);

    productCache.set(handle, promise);
    return promise;
  }

  function findVariantBySelectedOptions(product, form, picker) {
    const selectedOptions = collectSelectedOptionTexts(form, picker);
    if (!selectedOptions.length) return null;

    return product.variants.find((variant) => selectedOptions.every((value, index) => {
      if (!value) return true;
      return normalizeOptionText(variant.options[index]) === normalizeOptionText(value);
    })) || null;
  }

  function collectSelectedOptionTexts(form, picker) {
    if (!form.id) return [];

    const selectedOptions = [];
    const scope = picker || document;

    scope.querySelectorAll(`[form="${cssEscape(form.id)}"][data-option-position]:checked`).forEach((input) => {
      const position = Number(input.dataset.optionPosition || 0);
      if (!position) return;
      selectedOptions[position - 1] = getOptionLabelText(input);
    });

    return selectedOptions;
  }

  function getOptionLabelText(input) {
    const label = input.id ? document.querySelector(`label[for="${cssEscape(input.id)}"]`) : null;
    const focusedText = label?.querySelector('.ji-staffel__qty, .block-swatch__item, .swatch__label')?.textContent;
    const rawText = focusedText || label?.textContent || input.value || '';

    return rawText
      .replace(/Spare\s+\d+%/gi, '')
      .replace(/du sparst.*$/i, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function normalizeOptionText(value) {
    return String(value || '').replace(/\s+/g, ' ').trim().toLowerCase();
  }

  function getQuantity(form, formData) {
    const rawQuantity = formData.get('quantity') || document.querySelector(`[form="${cssEscape(form.id)}"][name="quantity"]`)?.value || '1';
    const quantity = Number.parseInt(rawQuantity, 10);
    return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  }

  function normalizeAddedItem(data, resolved, quantity) {
    const variant = resolved.variant || {};
    const product = resolved.product || {};
    const fallbackProductImage = typeof product.featured_image === 'object' ? product.featured_image?.src : product.featured_image;
    const fallbackVariantImage = typeof variant.featured_image === 'object' ? variant.featured_image?.src : variant.featured_image;
    const image = normalizeImageUrl(data.featured_image?.url || data.image || fallbackVariantImage || fallbackProductImage || findCurrentProductImage());
    const price = toCents(data.final_price ?? data.discounted_price ?? data.price ?? variant.price ?? product.price ?? 0);
    const compareAtPrice = toCents(variant.compare_at_price || 0);
    const variantTitle = cleanVariantTitle(data.variant_title || variant.title || '');
    const productUrl = data.url || `${rootUrl}products/${encodeURIComponent(config.productHandle)}?variant=${encodeURIComponent(variant.id || data.id || '')}`;

    return {
      id: String(data.id || variant.id || ''),
      productTitle: data.product_title || product.title || document.querySelector('.product-info__title, h1')?.textContent?.trim() || 'Produkt',
      variantTitle,
      quantity,
      price,
      compareAtPrice: compareAtPrice > price ? compareAtPrice : 0,
      image,
      url: productUrl
    };
  }

  function cleanVariantTitle(value) {
    const title = String(value || '').trim();
    return title && title !== 'Default Title' ? title : '';
  }

  function findCurrentProductImage() {
    const image = document.querySelector('.product-gallery img, .product img');
    return image?.currentSrc || image?.src || '';
  }

  async function refreshCartCount() {
    const cartUrl = config.cartJsonUrl || `${rootUrl}cart.js`;
    const response = await fetch(cartUrl, {
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' }
    });

    if (!response.ok) return null;

    const cart = await response.json();
    document.querySelectorAll('cart-count, [data-cart-count]').forEach((element) => {
      element.textContent = cart.item_count;
      element.hidden = false;
    });

    return cart;
  }

  function showConfirmation(item) {
    ensureOverlay();
    hydrateAddedItem(item);

    lastFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    overlay.hidden = false;
    document.documentElement.classList.add('ji-mobile-cart-confirmation-open');

    requestAnimationFrame(() => {
      overlay.classList.add('is-open');
      overlay.querySelector('[data-ji-close]')?.focus({ preventScroll: true });
    });

    loadRecommendations();
  }

  function ensureOverlay() {
    if (overlay) return;

    overlay = document.createElement('div');
    overlay.className = 'ji-mobile-cart-confirmation';
    overlay.hidden = true;
    overlay.innerHTML = `
      <div class="ji-mobile-cart-confirmation__backdrop" data-ji-close></div>
      <section class="ji-mobile-cart-confirmation__panel" role="dialog" aria-modal="true" aria-labelledby="ji-mobile-cart-confirmation-title">
        <header class="ji-mobile-cart-confirmation__header">
          <div class="ji-mobile-cart-confirmation__title-wrap">
            <span class="ji-mobile-cart-confirmation__check" aria-hidden="true">
              <svg viewBox="0 0 18 18" fill="none"><path d="M4.8 9.1 7.5 12 13.5 5.8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </span>
            <h2 id="ji-mobile-cart-confirmation-title" class="ji-mobile-cart-confirmation__title">Zum Warenkorb hinzugef\u00fcgt</h2>
          </div>
          <button type="button" class="ji-mobile-cart-confirmation__close" data-ji-close aria-label="Schliessen">
            <svg viewBox="0 0 24 24" fill="none"><path d="M6 6l12 12M18 6 6 18" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>
          </button>
        </header>

        <div class="ji-mobile-cart-confirmation__item">
          <a class="ji-mobile-cart-confirmation__media" data-ji-item-link href="#">
            <img data-ji-item-image alt="" loading="lazy">
          </a>
          <div class="ji-mobile-cart-confirmation__item-body">
            <p class="ji-mobile-cart-confirmation__product-title" data-ji-item-title></p>
            <p class="ji-mobile-cart-confirmation__variant" data-ji-item-variant></p>
            <div class="ji-mobile-cart-confirmation__price-row">
              <span class="ji-mobile-cart-confirmation__price" data-ji-item-price></span>
              <span class="ji-mobile-cart-confirmation__compare" data-ji-item-compare></span>
            </div>
          </div>
        </div>

        <div class="ji-mobile-cart-confirmation__actions">
          <a class="ji-mobile-cart-confirmation__button ji-mobile-cart-confirmation__button--secondary" data-ji-cart-link href="${escapeAttribute(config.cartUrl || `${rootUrl}cart`)}">Warenkorb anzeigen</a>
          <a class="ji-mobile-cart-confirmation__button ji-mobile-cart-confirmation__button--primary" href="${escapeAttribute(config.checkoutUrl || `${rootUrl}checkout`)}">Jetzt sicher zur Kasse</a>
        </div>

        <div class="ji-mobile-cart-confirmation__divider"></div>

        <div class="ji-mobile-cart-confirmation__recommendations" data-ji-recommendations hidden>
          <h3 class="ji-mobile-cart-confirmation__recommendations-title">Passt dazu</h3>
          <div class="ji-mobile-cart-confirmation__recommendations-track" data-ji-recommendations-track></div>
        </div>
      </section>`;

    document.body.appendChild(overlay);
    overlay.addEventListener('click', (event) => {
      if (event.target.closest('[data-ji-close]')) closeConfirmation();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && overlay && !overlay.hidden) closeConfirmation();
    });
  }

  function hydrateAddedItem(item) {
    const itemLink = overlay.querySelector('[data-ji-item-link]');
    const image = overlay.querySelector('[data-ji-item-image]');
    const media = overlay.querySelector('.ji-mobile-cart-confirmation__media');
    const title = overlay.querySelector('[data-ji-item-title]');
    const variant = overlay.querySelector('[data-ji-item-variant]');
    const price = overlay.querySelector('[data-ji-item-price]');
    const compare = overlay.querySelector('[data-ji-item-compare]');

    itemLink.href = item.url || '#';
    title.textContent = item.productTitle;
    variant.textContent = [item.variantTitle, item.quantity > 1 ? `Menge: ${item.quantity}` : ''].filter(Boolean).join(' / ');
    variant.hidden = variant.textContent === '';
    price.textContent = formatMoney(item.price);
    compare.textContent = item.compareAtPrice ? formatMoney(item.compareAtPrice) : '';

    if (item.image) {
      image.src = item.image;
      image.alt = item.productTitle;
      media.hidden = false;
    } else {
      image.removeAttribute('src');
      media.hidden = true;
    }
  }

  function closeConfirmation() {
    if (!overlay || overlay.hidden) return;

    overlay.classList.remove('is-open');
    document.documentElement.classList.remove('ji-mobile-cart-confirmation-open');

    window.setTimeout(() => {
      if (!overlay.classList.contains('is-open')) overlay.hidden = true;
      if (lastFocusedElement) lastFocusedElement.focus({ preventScroll: true });
    }, 240);
  }

  async function loadRecommendations() {
    const wrapper = overlay.querySelector('[data-ji-recommendations]');
    const track = overlay.querySelector('[data-ji-recommendations-track]');
    if (!wrapper || !track) return;

    wrapper.hidden = false;
    track.innerHTML = skeletonMarkup();

    const products = await getRecommendations();
    if (!products.length) {
      wrapper.hidden = true;
      track.innerHTML = '';
      return;
    }

    track.innerHTML = products.map(recommendationCard).join('');
  }

  async function getRecommendations() {
    if (recommendationsPromise) return recommendationsPromise;

    recommendationsPromise = (async () => {
      const complementary = await fetchRecommendations('complementary');
      const related = complementary.length ? [] : await fetchRecommendations('related');
      const seen = new Set([String(config.productId)]);

      return [...complementary, ...related].filter((product) => {
        const id = String(product.id || product.handle || product.url || '');
        if (!id || seen.has(id)) return false;
        seen.add(id);
        return true;
      }).slice(0, 8);
    })();

    return recommendationsPromise;
  }

  async function fetchRecommendations(intent) {
    const url = `${rootUrl}recommendations/products.json?product_id=${encodeURIComponent(config.productId)}&limit=8&intent=${encodeURIComponent(intent)}`;

    try {
      const response = await fetch(url, {
        credentials: 'same-origin',
        headers: { 'Accept': 'application/json' }
      });

      if (!response.ok) return [];
      const data = await response.json();
      return Array.isArray(data.products) ? data.products : [];
    } catch (error) {
      return [];
    }
  }

  function recommendationCard(product) {
    const displayedVariant = getDisplayedVariant(product);
    const image = normalizeImageUrl(typeof product.featured_image === 'object' ? product.featured_image?.src : product.featured_image || product.images?.[0] || '');
    const url = withVariantUrl(product.url || `${rootUrl}products/${encodeURIComponent(product.handle || '')}`, displayedVariant);
    const title = product.title || 'Produkt';
    const price = toCents(displayedVariant?.price ?? product.price_min ?? product.price ?? 0);
    const compareAtPrice = toCents(displayedVariant?.compare_at_price || 0);
    const vendor = product.vendor || '';

    return `
      <a class="ji-mobile-cart-confirmation__card" href="${escapeAttribute(url)}">
        <span class="ji-mobile-cart-confirmation__card-media">
          ${image ? `<img src="${escapeAttribute(image)}" alt="${escapeAttribute(title)}" loading="lazy">` : ''}
        </span>
        <span class="ji-mobile-cart-confirmation__card-title">${escapeHtml(title)}</span>
        ${vendor ? `<span class="ji-mobile-cart-confirmation__card-meta">${escapeHtml(vendor)}</span>` : ''}
        <span class="ji-mobile-cart-confirmation__card-price">
          <span>${formatMoney(price)}</span>
          ${compareAtPrice > price ? `<span class="ji-mobile-cart-confirmation__card-compare">${formatMoney(compareAtPrice)}</span>` : ''}
        </span>
      </a>`;
  }

  function getDisplayedVariant(product) {
    const variants = Array.isArray(product.variants) ? product.variants : [];
    if (!variants.length) return null;

    const availableVariants = variants.filter((variant) => variant.available !== false);
    const candidates = availableVariants.length ? availableVariants : variants;
    const targetPrice = product.price_min != null ? toCents(product.price_min) : Math.min(...candidates.map((variant) => toCents(variant.price)));

    return candidates.find((variant) => toCents(variant.price) === targetPrice) || candidates[0];
  }

  function withVariantUrl(url, variant) {
    if (!variant?.id) return url;

    try {
      const resolved = new URL(url, window.location.origin);
      if (!resolved.searchParams.has('variant')) resolved.searchParams.set('variant', variant.id);
      return `${resolved.pathname}${resolved.search}${resolved.hash}`;
    } catch (error) {
      const separator = String(url).includes('?') ? '&' : '?';
      return `${url}${separator}variant=${encodeURIComponent(variant.id)}`;
    }
  }

  function skeletonMarkup() {
    return Array.from({ length: 3 }, () => `
      <div class="ji-mobile-cart-confirmation__skeleton" aria-hidden="true">
        <div class="ji-mobile-cart-confirmation__skeleton-media"></div>
        <div class="ji-mobile-cart-confirmation__skeleton-line"></div>
        <div class="ji-mobile-cart-confirmation__skeleton-line ji-mobile-cart-confirmation__skeleton-line--short"></div>
      </div>`).join('');
  }

  function setSubmitterBusy(submitter, busy) {
    if (!(submitter instanceof HTMLElement)) return;

    submitter.toggleAttribute('aria-busy', busy);
    submitter.toggleAttribute('disabled', busy);
  }

  function toAjaxCartAddUrl(url) {
    const fallback = `${rootUrl}cart/add`;
    const normalized = String(url || fallback).replace(/\/$/, '');
    return normalized.endsWith('.js') ? normalized : `${normalized}.js`;
  }

  function normalizeRoot(value) {
    const root = String(value || '/');
    return root.endsWith('/') ? root : `${root}/`;
  }

  function normalizeImageUrl(value) {
    const url = String(value || '').trim();
    if (!url) return '';
    return url.startsWith('//') ? `https:${url}` : url;
  }

  function toCents(value) {
    if (value == null || value === '') return 0;
    if (typeof value === 'string') {
      const normalized = value.trim().replace(',', '.');
      if (normalized.includes('.')) return Math.round(Number(normalized) * 100) || 0;
    }
    return Number(value) || 0;
  }

  function formatMoney(cents) {
    const amount = Number(cents || 0) / 100;
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount).replace(/\s/g, '');
  }

  function escapeHtml(value) {
    return String(value ?? '').replace(/[&<>'"]/g, (character) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      "'": '&#39;',
      '"': '&quot;'
    }[character]));
  }

  function escapeAttribute(value) {
    return escapeHtml(value);
  }

  function cssEscape(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(String(value));
    return String(value).replace(/"/g, '\\"');
  }

  function onReady(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  }
})();